export const PARTY_PASS_CELEBRATION_STORAGE_PREFIX =
  "party-pass-celebration-seen";
export const PARTY_PASS_CELEBRATION_SEEN_VALUE = "1";
export const PARTY_PASS_CHECKOUT_SESSION_PLACEHOLDER =
  "{CHECKOUT_SESSION_ID}" as const;

export type PartyPassUpgradeUiState =
  | "celebrate"
  | "confirm_failed"
  | "canceled"
  | "none";

export interface PartyPassUpgradeUiStateInput {
  upgrade?: string | null;
  isPaid: boolean;
  isOwner: boolean;
}

export interface CelebrationStorageReader {
  getItem(key: string): string | null;
}

export interface CelebrationStorageWriter {
  setItem(key: string, value: string): void;
}

export type PartyPassCelebrationBurstTint = "gold" | "gold-light" | "cream";

export interface PartyPassCelebrationBurstPiece {
  id: number;
  x: number;
  y: number;
  rotate: number;
  delay: number;
  width: number;
  duration: number;
  tint: PartyPassCelebrationBurstTint;
}

const UPGRADE_SEARCH_PARAMS = ["upgrade", "session_id"] as const;

function normalizePartyId(partyId: string): string {
  return partyId.trim();
}

export function getPartyPassUpgradeUiState(
  input: PartyPassUpgradeUiStateInput,
): PartyPassUpgradeUiState {
  if (input.upgrade === "canceled") {
    return "canceled";
  }

  if (input.upgrade !== "success") {
    return "none";
  }

  if (!input.isOwner) {
    return "none";
  }

  return input.isPaid ? "celebrate" : "confirm_failed";
}

export function getPartyPassCelebrationStorageKey(partyId: string): string {
  return `${PARTY_PASS_CELEBRATION_STORAGE_PREFIX}:${normalizePartyId(partyId)}`;
}

export function isPartyPassCelebrationSeen(
  storage: CelebrationStorageReader,
  partyId: string,
): boolean {
  const normalized = normalizePartyId(partyId);
  if (!normalized) {
    return false;
  }

  return (
    storage.getItem(getPartyPassCelebrationStorageKey(normalized)) ===
    PARTY_PASS_CELEBRATION_SEEN_VALUE
  );
}

export function markPartyPassCelebrationSeen(
  storage: CelebrationStorageWriter,
  partyId: string,
): void {
  const normalized = normalizePartyId(partyId);
  if (!normalized) {
    return;
  }

  storage.setItem(
    getPartyPassCelebrationStorageKey(normalized),
    PARTY_PASS_CELEBRATION_SEEN_VALUE,
  );
}

export function stripPartyPassUpgradeSearchParams(search: string): string {
  const trimmed = search.startsWith("?") ? search.slice(1) : search;
  if (!trimmed) {
    return "";
  }

  const params = new URLSearchParams(trimmed);
  for (const key of UPGRADE_SEARCH_PARAMS) {
    params.delete(key);
  }

  const next = params.toString();
  return next ? `?${next}` : "";
}

export function consumePartyPassUpgradeParamsFromUrl(
  location: {
    pathname: string;
    search: string;
    hash: string;
  },
  replaceState: (url: string) => void,
): void {
  const nextSearch = stripPartyPassUpgradeSearchParams(location.search);
  if (nextSearch === location.search) {
    return;
  }

  replaceState(`${location.pathname}${nextSearch}${location.hash}`);
}

export function getPartyPassCheckoutSuccessPath(partyId: string): string {
  return `/parties/${partyId}?upgrade=success&session_id=${PARTY_PASS_CHECKOUT_SESSION_PLACEHOLDER}`;
}

export function getPartyPassCheckoutCancelPath(partyId: string): string {
  return `/parties/${partyId}/settings?upgrade=canceled`;
}

export function getPartyPassCelebrationBurstPieces(
  count = 24,
): PartyPassCelebrationBurstPiece[] {
  if (!Number.isFinite(count) || count <= 0) {
    return [];
  }

  const tints: PartyPassCelebrationBurstTint[] = [
    "gold",
    "gold-light",
    "cream",
  ];

  return Array.from({ length: count }, (_, id) => {
    const column = id % 8;
    const row = Math.floor(id / 8);
    return {
      id,
      x: column * 12.5 + (id % 3) * 1.5,
      y: 18 + row * 22 + (id % 5) * 4,
      rotate: Math.round((id * 37) % 180 - 90),
      delay: (id % 8) * 0.045,
      width: 8 + (id % 4) * 4,
      duration: 0.95 + (id % 5) * 0.08,
      tint: tints[id % tints.length] ?? "gold",
    };
  });
}
